import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from '../cn';
import { getPopoverPositionStyle } from '../popoverPosition';
import { useAnchorRect } from '../useAnchorRect';

type AutocompleteProps<T> = {
  options: T[],
  value?: T | null,
  defaultValue?: T | null,
  onChange?: (event: any, newValue: T | string | null) => void,
  onInputChange?: (event: any, newInputValue: string) => void,
  getOptionLabel?: (option: T) => string,
  isOptionEqualToValue?: (option: T, value: T) => boolean,
  freeSolo?: boolean,
  filterOptions?: (options: T[], state: { inputValue: string }) => T[],
  renderInput: (params: any) => ReactNode,
  fullWidth?: boolean,
  size?: 'small' | 'medium',
  className?: string,
  disabled?: boolean,
  noOptionsText?: ReactNode,
  clearOnEscape?: boolean,
  autoHighlight?: boolean,
};

// Tailwind port of MUI Autocomplete, API-compatible for this repo's usages:
// options/value/onChange(event,newValue)/getOptionLabel/isOptionEqualToValue/
// freeSolo/filterOptions/renderInput/fullWidth/size. Drives its own dropdown and
// calls renderInput(params) with props the Tailwind TextField understands, so call
// sites stay `<TextField {...params} label=... />`. Portals to #root.
function Autocomplete<T>({
  options,
  value = null,
  defaultValue = null,
  onChange,
  onInputChange,
  getOptionLabel,
  isOptionEqualToValue,
  freeSolo = false,
  filterOptions,
  renderInput,
  fullWidth = false,
  size = 'medium',
  className = '',
  disabled = false,
  noOptionsText = 'No options',
}: AutocompleteProps<T>) {
  const label = (o: T | null): string => {
    if (o === null || o === undefined) return '';
    return getOptionLabel ? getOptionLabel(o) : (typeof o === 'string' ? o : String(o));
  };

  const [inputValue, setInputValue] = useState<string>(label(value ?? defaultValue));
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selectedLabel = label(value);
  // keep the field text in sync with the selected value when not actively typing
  useEffect(() => { if (!open) setInputValue(selectedLabel); }, [selectedLabel, open]);

  // Live rect: follows the anchor when a dialog body or the page scrolls.
  const rect = useAnchorRect(open, rootRef, [inputValue]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      if (listRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const filtered = filterOptions
    ? filterOptions(options, { inputValue })
    : options.filter((o) => {
        const optLabel = label(o);
        if (!optLabel || !inputValue) return true;
        return optLabel.toLowerCase().includes(inputValue.toLowerCase());
      });

  const select = (event: any, option: T) => {
    onChange?.(event, option);
    setInputValue(label(option));
    setOpen(false);
  };

  const handleInputChange = (event: any, v: string) => {
    setInputValue(v);
    setOpen(true);
    setActive(-1);
    onInputChange?.(event, v);
    if (v === '' && value !== null) onChange?.(event, null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') {
      if (active >= 0 && filtered[active]) { e.preventDefault(); select(e, filtered[active]); }
      else if (freeSolo) { onChange?.(e, inputValue); setOpen(false); }
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  const handleBlur = (e: any) => {
    // let click-on-option register first
    setTimeout(() => {
      if (freeSolo) onChange?.(e, inputValue);
      else setInputValue(selectedLabel);
    }, 120);
  };

  const params = {
    value: inputValue,
    disabled,
    fullWidth: true,
    size,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, e.target.value),
    onFocus: () => setOpen(true),
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
  };

  const popoverPosition = rect ? getPopoverPositionStyle(rect) : null;

  return (
    <div ref={rootRef} className={cn('relative', fullWidth && 'w-full', className)}>
      {renderInput(params)}
      {open && rect && createPortal(
        <ul
          ref={listRef}
          role="listbox"
          style={popoverPosition!.style}
          className={cn('ds-menu scrollbar-thin', popoverPosition!.className)}
        >
          {filtered.length === 0 ? (
            <li className="ds-menu-empty">{noOptionsText}</li>
          ) : filtered.map((o, i) => {
            const selected = value != null && (isOptionEqualToValue ? isOptionEqualToValue(o, value) : o === value);
            return (
              <li
                key={i}
                role="option"
                aria-selected={selected}
                data-ds-active={active === i ? 'true' : undefined}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); select(e, o); }}
                className="ds-menu-item"
              >
                {label(o)}
              </li>
            );
          })}
        </ul>,
        document.getElementById('root') ?? document.body,
      )}
    </div>
  );
}

export default Autocomplete;
